import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMobileTheme } from '../../../hooks/useMobileTheme';
import type { MobileThemeKey } from '../../../hooks/useMobileTheme';
import HeroLiveStats from './HeroLiveStats';

type ThemeKey = MobileThemeKey;

interface DesktopHeroTheme {
  bgImage: string;
  overlay: string;
  badgeBg: string;
  badgeBorder: string;
  badgeText: string;
  badgeIcon: string;
  badgeIconColor: string;
  accentText: string;
  titleText: string;
  subText: string;
  ctaPrimary: string;
  ctaSecondary: string;
  waitBg: string;
  waitBorder: string;
  waitNumBg: string;
  waitNumText: string;
  waitTitle: string;
  waitSub: string;
  iconCardBg: string;
  iconCardHover: string;
  iconCardIconBg: string;
  iconCardIconHover: string;
  iconCardText: string;
  iconCardTextHover: string;
  statsCardBg: string;
  statsCardBorder: string;
}

const THEMES: Record<ThemeKey, DesktopHeroTheme> = {
  tech: {
    bgImage: 'https://public.readdy.ai/ai/img_res/e3123554e751ebf9f43b4c8b427816b3.jpg',
    overlay: 'bg-gradient-to-b from-black/65 via-black/50 to-black/70',
    badgeBg: 'bg-teal-400/25',
    badgeBorder: 'border-teal-300/35',
    badgeText: 'text-teal-100',
    badgeIcon: 'ri-sparkling-2-fill',
    badgeIconColor: 'text-yellow-300',
    accentText: 'text-teal-300',
    titleText: 'text-white',
    subText: 'text-white/70',
    ctaPrimary: 'bg-teal-500 hover:bg-teal-400 text-white shadow-teal-500/40',
    ctaSecondary: 'bg-white/15 hover:bg-white/25 text-white border border-white/30',
    waitBg: 'bg-black/50 group-hover:bg-black/60',
    waitBorder: 'border-amber-400/60 hover:border-amber-400',
    waitNumBg: 'bg-amber-400 group-hover:bg-amber-300',
    waitNumText: 'text-gray-900',
    waitTitle: 'text-white',
    waitSub: 'text-white/60',
    iconCardBg: 'bg-white/90',
    iconCardHover: 'hover:bg-white',
    iconCardIconBg: 'bg-teal-500',
    iconCardIconHover: 'group-hover:bg-teal-600',
    iconCardText: 'text-gray-700',
    iconCardTextHover: 'group-hover:text-teal-600',
    statsCardBg: 'bg-black/50 hover:bg-black/55',
    statsCardBorder: 'border-white/15',
  },
  pink: {
    bgImage: 'https://readdy.ai/api/search-image?query=ultra%20clean%20minimal%20Scandinavian%20Nordic%20apartment%20interior%20bedroom%20with%20pale%20blush%20rose%20white%20walls%2C%20soft%20dusty%20pink%20linen%20pillows%20on%20white%20bed%2C%20light%20maple%20wood%20floors%2C%20gentle%20morning%20sunlight%20streaming%20through%20sheer%20white%20curtains%2C%20very%20soft%20airy%20bright%20photography%2C%20simple%20elegant%20minimal%20furniture%20in%20pale%20neutral%20tones%2C%20professional%20real%20estate%20photography%2C%20light%20and%20airy%20aesthetic%2C%20no%20clutter%2C%20calm%20peaceful%20feminine%20minimal%20design%2C%20white%20background%20with%20very%20subtle%20warm%20pink%20accents&width=1920&height=1080&seq=desktop-hero-pink-minimal-2026&orientation=landscape',
    overlay: 'bg-gradient-to-b from-white/25 via-rose-50/15 to-rose-100/30',
    badgeBg: 'bg-white/80',
    badgeBorder: 'border-rose-100',
    badgeText: 'text-rose-400',
    badgeIcon: 'ri-heart-line',
    badgeIconColor: 'text-rose-300',
    accentText: 'text-rose-400',
    titleText: 'text-gray-900',
    subText: 'text-gray-500',
    ctaPrimary: 'bg-rose-400 hover:bg-rose-500 text-white shadow-rose-300/30',
    ctaSecondary: 'bg-white/80 hover:bg-white text-rose-500 border border-rose-100',
    waitBg: 'bg-white/80 group-hover:bg-white/95',
    waitBorder: 'border-rose-100 hover:border-rose-200',
    waitNumBg: 'bg-rose-300 group-hover:bg-rose-400',
    waitNumText: 'text-white',
    waitTitle: 'text-gray-800',
    waitSub: 'text-gray-400',
    iconCardBg: 'bg-white/85',
    iconCardHover: 'hover:bg-white',
    iconCardIconBg: 'bg-rose-300',
    iconCardIconHover: 'group-hover:bg-rose-400',
    iconCardText: 'text-gray-700',
    iconCardTextHover: 'group-hover:text-rose-400',
    statsCardBg: 'bg-white/75 hover:bg-white/90',
    statsCardBorder: 'border-rose-50',
  },
  white: {
    bgImage: 'https://readdy.ai/api/search-image?query=stunning%20modern%20apartment%20interior%20with%20floor%20to%20ceiling%20windows%20overlooking%20lush%20green%20park%2C%20abundant%20indoor%20tropical%20plants%20monstera%20fiddle%20leaf%20fig%20in%20white%20ceramic%20pots%2C%20teal%20and%20sage%20green%20accent%20cushions%20on%20light%20linen%20sofa%2C%20warm%20oak%20wood%20floors%2C%20natural%20diffused%20daylight%20streaming%20through%20sheer%20curtains%2C%20airy%20Japandi%20minimalist%20style%2C%20soft%20green%20and%20white%20tones%2C%20professional%20architectural%20photography%2C%20calm%20serene%20atmosphere%2C%20fresh%20botanical%20living%20room&width=1920&height=1080&seq=white-teal-desktop-hero-2026&orientation=landscape',
    // 左側加重遮罩讓文字清晰，右側保留圖片感
    overlay: 'bg-gradient-to-r from-white/92 via-white/78 to-white/20',
    badgeBg: 'bg-white/90',
    badgeBorder: 'border-teal-200',
    badgeText: 'text-teal-700',
    badgeIcon: 'ri-sparkling-2-fill',
    badgeIconColor: 'text-teal-500',
    accentText: 'text-teal-600',
    titleText: 'text-gray-900',
    subText: 'text-gray-600',
    ctaPrimary: 'bg-teal-700 hover:bg-teal-800 text-white shadow-teal-700/20',
    ctaSecondary: 'bg-white/85 hover:bg-white text-teal-700 border border-teal-200',
    waitBg: 'bg-white/80 group-hover:bg-white/95',
    waitBorder: 'border-amber-500/50 hover:border-amber-500/80',
    waitNumBg: 'bg-amber-400 group-hover:bg-amber-300',
    waitNumText: 'text-gray-900',
    waitTitle: 'text-gray-800',
    waitSub: 'text-gray-500',
    iconCardBg: 'bg-white/90',
    iconCardHover: 'hover:bg-white',
    iconCardIconBg: 'bg-teal-600',
    iconCardIconHover: 'group-hover:bg-teal-700',
    iconCardText: 'text-gray-700',
    iconCardTextHover: 'group-hover:text-teal-600',
    statsCardBg: 'bg-white/80 hover:bg-white/95',
    statsCardBorder: 'border-teal-100',
  },
};

const QUICK_LINKS = [
  { to: '/publish', icon: 'ri-home-4-line', label: '我要出租' },
  { to: '/regions', icon: 'ri-search-line', label: '我要找房' },
  { to: '/ai-match', icon: 'ri-sparkling-2-fill', label: 'AI 媒合' },
  { to: '/landlord-dashboard', icon: 'ri-wallet-3-line', label: '我的房地產錢包' },
];

interface DesktopHeroProps {
  tenantMatchCount: number | null;
  onShowWaitingModal: () => void;
  onShowGuestOnboarding: () => void;
}

export default function DesktopHero({ tenantMatchCount, onShowWaitingModal, onShowGuestOnboarding }: DesktopHeroProps) {
  const { theme } = useMobileTheme();
  const t = THEMES[theme];

  // ── Blur 預載效果：圖片未載入時模糊，載入後漸漸清晰 ──
  const [imgLoaded, setImgLoaded] = useState(false);

  // 切換主題時重置，讓新圖片重新跑 blur 動畫
  useEffect(() => {
    setImgLoaded(false);
  }, [theme]);

  return (
    <section className="relative min-h-screen hidden lg:flex items-center justify-center overflow-hidden">
      {/* ── 背景圖 ── */}
      <div className="absolute inset-0">
        {/* 佔位色層：圖片載入前顯示，避免白色閃爍 */}
        <div
          className="absolute inset-0 transition-opacity duration-700"
          style={{
            background: theme === 'tech'
              ? 'linear-gradient(135deg, #0f172a 0%, #134e4a 100%)'
              : theme === 'pink'
              ? 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)'
              : 'linear-gradient(135deg, #f0fdf4 0%, #f0fdfa 100%)',
            opacity: imgLoaded ? 0 : 1,
          }}
        />
        <img
          key={theme}
          src={t.bgImage}
          alt="Hero Background"
          onLoad={() => setImgLoaded(true)}
          style={{
            filter: imgLoaded ? 'blur(0px)' : 'blur(24px)',
            transform: imgLoaded ? 'scale(1)' : 'scale(1.08)',
            opacity: imgLoaded ? 1 : 0.4,
            transition: 'filter 0.9s ease, transform 0.9s ease, opacity 0.7s ease',
            willChange: 'filter, transform, opacity',
          }}
          className="w-full h-full object-cover object-center"
        />
        {/* 主遮罩：白色主題用強力左-右漸層保證文字可讀 */}
        {theme === 'white' ? (
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to right, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.95) 25%, rgba(255,255,255,0.85) 45%, rgba(255,255,255,0.50) 65%, rgba(255,255,255,0.12) 100%)',
            }}
          />
        ) : (
          <div className={`absolute inset-0 ${t.overlay} transition-all duration-700`} />
        )}
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-16">

          {/* ── 左側文字 ── */}
          <div className="flex-1 w-full text-center lg:text-left">

            {/* H1 主標 */}
            <h1 className={`text-4xl sm:text-5xl md:text-7xl font-black ${t.titleText} mb-4 leading-tight tracking-tight transition-colors duration-500`}>
              一個由AI媒合<br />
              <span className={t.accentText}>零仲介費</span><br className="sm:hidden" /> 房地產平台
            </h1>

            {/* 副標 */}
            <p className={`text-sm md:text-lg ${t.subText} mb-7 leading-relaxed transition-colors duration-500`}>
              免費刊登房源・直接聯繫・AI 智能配對
            </p>

            {/* 租客等待橫幅 */}
            <button
              type="button"
              onClick={onShowWaitingModal}
              className={`group flex items-stretch mb-4 rounded-2xl overflow-hidden border-2 ${t.waitBorder} transition-all duration-300 hover:-translate-y-1 cursor-pointer w-full max-w-lg text-left`}
            >
              <div className={`flex flex-col items-center justify-center px-4 py-3 ${t.waitNumBg} transition-colors min-w-[80px] flex-shrink-0`}>
                {tenantMatchCount === null ? (
                  <i className={`ri-loader-4-line text-xl ${t.waitNumText} animate-spin`}></i>
                ) : tenantMatchCount > 0 ? (
                  <>
                    <span className={`text-3xl font-black ${t.waitNumText} leading-none tabular-nums`}>{tenantMatchCount}</span>
                    <span className={`text-xs font-bold ${t.waitNumText} mt-0.5 whitespace-nowrap`}>位租客</span>
                  </>
                ) : (
                  <>
                    <i className={`ri-user-search-line text-2xl ${t.waitNumText}`}></i>
                    <span className={`text-xs font-bold ${t.waitNumText} mt-0.5 whitespace-nowrap`}>租客媒合</span>
                  </>
                )}
              </div>
              <div className={`flex flex-col justify-center px-4 py-3 ${t.waitBg} transition-colors flex-1`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                  </span>
                  <span className={`${t.waitTitle} font-black text-sm whitespace-nowrap`}>
                    {tenantMatchCount !== null && tenantMatchCount > 0 ? '位租客正在等待您的房子！' : 'AI 正在為您媒合租客'}
                  </span>
                </div>
                <span className={`${t.waitSub} text-xs`}>點擊查看等待中的租客需求</span>
              </div>
            </button>

            {/* 副標語 */}
            <p className={`text-sm md:text-lg ${t.subText} font-light mb-5 transition-colors duration-500`}>
              免費刊登房源・安心找家・零仲介費直媒合
            </p>

            {/* 四個快捷入口 */}
            <div className="grid grid-cols-4 gap-2 w-full max-w-2xl">
              {QUICK_LINKS.map((item) =>
                item.label === '我要出租' ? (
                  <button
                    key={item.label}
                    type="button"
                    onClick={onShowGuestOnboarding}
                    className={`flex flex-col items-center gap-1.5 px-1 py-3 ${t.iconCardBg} ${t.iconCardHover} rounded-xl transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group`}
                  >
                    <div className={`w-9 h-9 flex items-center justify-center ${t.iconCardIconBg} ${t.iconCardIconHover} rounded-lg transition-colors`}>
                      <i className={`${item.icon} text-lg text-white`}></i>
                    </div>
                    <span className={`text-xs font-semibold ${t.iconCardText} ${t.iconCardTextHover} whitespace-nowrap transition-colors`}>{item.label}</span>
                  </button>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex flex-col items-center gap-1.5 px-1 py-3 ${t.iconCardBg} ${t.iconCardHover} rounded-xl transition-all duration-200 hover:-translate-y-0.5 cursor-pointer group`}
                  >
                    <div className={`w-9 h-9 flex items-center justify-center ${t.iconCardIconBg} ${t.iconCardIconHover} rounded-lg transition-colors`}>
                      <i className={`${item.icon} text-lg text-white`}></i>
                    </div>
                    <span className={`text-xs font-semibold ${t.iconCardText} ${t.iconCardTextHover} whitespace-nowrap transition-colors`}>{item.label}</span>
                  </Link>
                )
              )}
            </div>
          </div>

          {/* ── 右側 Live Stats 卡片 ── */}
          <div className="hidden lg:flex flex-col w-80 xl:w-96 flex-shrink-0">
            <HeroLiveStats />
          </div>
        </div>
      </div>
    </section>
  );
}
